import { useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { createMessagesI18n } from './i18n';
import TextEditorVariables from '../../components/TextEditorVariables/TextEditorVariables';

export default function CreateCreateMessagesModal({
  Modal,
  Input,
  Dropdown,
  Button,
  show,
  mode = 'create', // 'create' | 'edit'
  initialData = null,
  onClose,
  onSave,
  variableGroups = [],
}) {
  const { t, language: cmLanguage } = useScopedTranslation(createMessagesI18n);

  const messageTypeOptions = useMemo(
    () => [
      { label: t('cm_type_whatsapp'), value: 'whatsapp' },
      { label: t('cm_type_sms'), value: 'sms' },
      { label: t('cm_type_email'), value: 'email' },
    ],
    [cmLanguage]
  );

  const statusOptions = useMemo(
    () => [
      { label: t('cm_status_active'), value: 'Activo' },
      { label: t('cm_status_inactive'), value: 'Inactivo' },
    ],
    [cmLanguage]
  );

  const [form, setForm] = useState({
    name: '',
    messageType: '',
    status: '',
  });

  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const textAreaRef = useRef(null);

  // Hydrate form on open / mode change
  useEffect(() => {
    if (!show) return;

    if (mode === 'edit' && initialData) {
      setForm({
        name: initialData.name || '',
        messageType: initialData.messageType || '',
        status: initialData.status || '',
      });
      setContent(initialData.content || '');
    } else {
      setForm({
        name: '',
        messageType: '',
        status: statusOptions[0]?.value || '',
      });
      setContent('');
    }
  }, [show, mode, initialData, statusOptions]);

  const setField = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

  const isValid =
    form.name.trim().length > 2 &&
    !!form.messageType &&
    !!form.status &&
    content.trim().length > 0;

  const handleInsertVariable = (placeholder) => {
    if (!placeholder) return;
    const textarea = textAreaRef.current;
    if (!textarea) {
      setContent((prev) => prev + placeholder);
      return;
    }

    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    const current = textarea.value || '';

    const next =
      current.substring(0, start) + placeholder + current.substring(end);

    setContent(next);

    // restore cursor just after inserted text
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = start + placeholder.length;
      textarea.selectionStart = cursorPos;
      textarea.selectionEnd = cursorPos;
    });
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSave?.({
        id: initialData?.id,
        name: form.name.trim(),
        messageType: form.messageType,
        status: form.status,
        content,
        mode,
      });
      setSaving(false);
      onClose?.();
    } catch (error) {
      console.error('Error saving message template:', error);
      setSaving(false);
      alert(t('cm_modal_saveError'));
    }
  };

  if (!Modal || !Input || !Dropdown || !Button) return null;
  if (!show) return null;

  const title =
    mode === 'edit'
      ? t('cm_modal_titleEdit')
      : t('cm_modal_titleCreate');

  const primaryLabel = saving
    ? t('cm_modal_saving')
    : mode === 'edit'
      ? t('cm_modal_saveEdit')
      : t('cm_modal_saveCreate');

  return (
    <Modal
      title={title}
      show={show}
      onClose={onClose}
      primaryBtnTxt={primaryLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('cm_modal_cancel')}
      secondaryBtnAction={onClose}
      width={960}
      height='80vh'
    >
      <div
        className='d-flex flex-column gap-3'
        style={{ height: '100%', maxHeight: '72vh' }}
      >
        {/* TOP: name + type + status */}
        <div className='row g-2'>
          <div className='col-12 col-md-6'>
            <Input
              label={t('cm_modal_nameLabel')}
              placeholder={t('cm_modal_namePlaceholder')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>
          <div className='col-12 col-md-3'>
            <Dropdown
              label={t('cm_modal_typeLabel')}
              placeholder={t('cm_modal_typePlaceholder')}
              options={messageTypeOptions}
              value={messageTypeOptions.find(
                (o) => o.value === form.messageType
              )}
              onChange={(selected) =>
                setField('messageType', selected?.value ?? '')
              }
            />
          </div>
          <div className='col-12 col-md-3'>
            <Dropdown
              label={t('cm_modal_statusLabel')}
              placeholder={t('cm_modal_statusPlaceholder')}
              options={statusOptions}
              value={statusOptions.find(
                (o) => o.value === form.status
              )}
              onChange={(selected) => setField('status', selected?.value ?? '')}
            />
          </div>
        </div>

        {/* BOTTOM: content + variables */}
        <div className='d-flex gap-3 flex-grow-1' style={{ minHeight: 320 }}>
          {/* LEFT: textarea editor */}
          <div className='flex-grow-1 d-flex flex-column gap-2'>
            <div className='fw-medium mb-1'>{t('cm_modal_contentTitle')}</div>
            <div className='border rounded-3 flex-grow-1'>
              <textarea
                ref={textAreaRef}
                className='form-control h-100'
                style={{
                  border: 'none',
                  resize: 'none',
                  minHeight: 260,
                }}
                placeholder={t('cm_modal_contentPlaceholder')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
          </div>

          {/* RIGHT: variables */}
          <div
            className='d-flex flex-column'
            style={{ width: 320, flexShrink: 0 }}
          >
            <TextEditorVariables
              variableGroups={variableGroups}
              onInsert={handleInsertVariable}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
